import React, { useEffect, useState } from 'react';
import { statsAPI } from '../lib/api';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  BarChart, Bar, LineChart, Line, PieChart, Pie, Cell,
  XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend,
} from 'recharts';
import {
  TrendingUp, Users, Calendar, Award, FileCheck, Loader2,
} from 'lucide-react';
import { toast } from 'sonner';
import Layout from '../components/Layout';

const STATUS_COLORS = {
  upcoming: '#0ea5e9',
  active: '#10b981',
  completed: '#64748b',
  unknown: '#94a3b8',
};

const FUNNEL_COLORS = {
  submitted: '#94a3b8',
  approved: '#0ea5e9',
  paid: '#10b981',
  rejected: '#ef4444',
};

function Kpi({ icon: Icon, label, value, sub, testid }) {
  return (
    <Card className="border-border" data-testid={testid}>
      <CardContent className="p-5">
        <div className="flex items-start justify-between">
          <div>
            <p className="text-sm text-muted-foreground">{label}</p>
            <p className="text-3xl font-bold text-foreground mt-1">{value}</p>
            {sub && <p className="text-xs text-muted-foreground mt-1">{sub}</p>}
          </div>
          <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
            <Icon className="w-5 h-5 text-primary" />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

export default function Analytics() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchAnalytics();
  }, []);

  const fetchAnalytics = async () => {
    try {
      const res = await statsAPI.analytics();
      setData(res.data);
    } catch (e) {
      toast.error('Failed to load analytics');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Layout activeNav="analytics">
        <div className="flex items-center justify-center min-h-[60vh]">
          <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
        </div>
      </Layout>
    );
  }

  if (!data) return null;

  const k = data.overall_kpis;
  const statusData = Object.entries(data.events_by_status).map(([name, value]) => ({ name, value }));
  // Sponsorship funnel (EOI-driven) — stages in order
  const funnelOrder = ['submitted', 'approved', 'paid', 'rejected'];
  const funnelData = funnelOrder.map((status) => ({
    status,
    count: data.sponsorship_funnel?.[status] || 0,
  }));
  const conv = data.sponsorship_conversion || { approval_rate: 0, payment_rate: 0, overall_paid_rate: 0 };
  const tierFunnel = data.sponsorship_funnel_by_tier || [];
  const memberSummary = data.member_sponsorship_summary || { paid_count: 0, unpaid_count: 0, paid_revenue: 0 };
  const fb = data.feedback_summary;
  const feedbackData = [
    { question: 'Objectives met', yes: fb.objectives_met_yes, no: fb.objectives_met_no },
    { question: 'Evidence-based', yes: fb.evidence_based_yes, no: fb.evidence_based_no },
    { question: 'No commercial bias', yes: fb.commercial_bias_no, no: fb.commercial_bias_yes },
    { question: 'Clinical influence', yes: fb.clinical_influence_yes, no: fb.clinical_influence_no },
  ];

  return (
    <Layout activeNav="analytics">
      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8 flex-wrap gap-4">
          <div>
            <h1 className="text-3xl font-bold text-foreground mb-2 flex items-center gap-2">
              <TrendingUp className="w-7 h-7" />
              Analytics
            </h1>
            <p className="text-muted-foreground">
              Practice-wide performance: attendance, feedback, sponsorship funnel.
            </p>
          </div>
        </div>

        {/* KPIs */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 mb-8" data-testid="analytics-kpis">
          <Kpi icon={Calendar} label="Total Events" value={k.total_events} testid="kpi-events" />
          <Kpi icon={Users} label="Registrations" value={k.total_attendees}
               sub={`${k.total_checked_in} checked in (${k.check_in_rate}%)`} testid="kpi-attendees" />
          <Kpi icon={FileCheck} label="Feedback Submitted" value={k.total_feedback}
               sub={`${k.feedback_rate}% of checked-in`} testid="kpi-feedback" />
          <Kpi icon={Award} label="Certificates Issued" value={k.total_certs_downloaded + k.total_certs_sent}
               sub={`${k.total_certs_downloaded} downloaded, ${k.total_certs_sent} emailed`} testid="kpi-certs" />
        </div>

        <div className="grid lg:grid-cols-2 gap-6 mb-6">
          {/* Events over time */}
          <Card className="border-border" data-testid="events-over-time-chart">
            <CardHeader>
              <CardTitle className="text-lg">Events &amp; registrations (last 12 months)</CardTitle>
              <CardDescription>Trend of activity across the platform</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="h-[260px]">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart
                    data={data.events_over_time.map((e, i) => ({
                      month: e.month,
                      events: e.count,
                      attendees: data.attendees_over_time[i]?.count || 0,
                    }))}
                    margin={{ top: 10, right: 10, left: -10, bottom: 0 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                    <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                    <Tooltip />
                    <Legend />
                    <Line type="monotone" dataKey="events" stroke="#0ea5e9" strokeWidth={2} dot={{ r: 3 }} />
                    <Line type="monotone" dataKey="attendees" stroke="#10b981" strokeWidth={2} dot={{ r: 3 }} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>

          {/* Status breakdown */}
          <Card className="border-border" data-testid="events-by-status-chart">
            <CardHeader>
              <CardTitle className="text-lg">Events by status</CardTitle>
              <CardDescription>Lifecycle distribution</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="h-[260px]">
                {statusData.length === 0 ? (
                  <div className="flex items-center justify-center h-full text-muted-foreground text-sm">
                    No events yet
                  </div>
                ) : (
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={statusData}
                        dataKey="value"
                        nameKey="name"
                        cx="50%"
                        cy="50%"
                        outerRadius={90}
                        label={(entry) => `${entry.name} (${entry.value})`}
                      >
                        {statusData.map((entry) => (
                          <Cell key={entry.name} fill={STATUS_COLORS[entry.name] || '#94a3b8'} />
                        ))}
                      </Pie>
                      <Tooltip />
                    </PieChart>
                  </ResponsiveContainer>
                )}
              </div>
            </CardContent>
          </Card>
        </div>

        <div className="grid lg:grid-cols-2 gap-6 mb-6">
          {/* Sponsorship funnel */}
          <Card className="border-border" data-testid="sponsorship-funnel-chart">
            <CardHeader className="flex flex-row items-start justify-between flex-wrap gap-2">
              <div>
                <CardTitle className="text-lg">Sponsorship funnel (EOI → Paid)</CardTitle>
                <CardDescription>Conversion across all events</CardDescription>
              </div>
              <Badge variant="secondary" className="bg-emerald-100 text-emerald-800" data-testid="sponsorship-paid-revenue-badge">
                Paid: AUD ${Number(data.sponsorship_total_paid_revenue || 0).toLocaleString()}
              </Badge>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-3 gap-2 mb-4 text-center" data-testid="sponsorship-conversion-strip">
                <div className="rounded-md bg-muted/50 px-2 py-2">
                  <p className="text-xs text-muted-foreground">Submitted → Approved</p>
                  <p className="font-semibold text-base mt-0.5">{conv.approval_rate}%</p>
                </div>
                <div className="rounded-md bg-muted/50 px-2 py-2">
                  <p className="text-xs text-muted-foreground">Approved → Paid</p>
                  <p className="font-semibold text-base mt-0.5">{conv.payment_rate}%</p>
                </div>
                <div className="rounded-md bg-muted/50 px-2 py-2">
                  <p className="text-xs text-muted-foreground">Overall paid rate</p>
                  <p className="font-semibold text-base mt-0.5">{conv.overall_paid_rate}%</p>
                </div>
              </div>
              <div className="h-[220px]">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={funnelData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis dataKey="status" tick={{ fontSize: 11 }} />
                    <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                    <Tooltip />
                    <Bar dataKey="count" name="EOIs">
                      {funnelData.map((d) => (
                        <Cell key={d.status} fill={FUNNEL_COLORS[d.status] || '#94a3b8'} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>

          {/* Top events */}
          <Card className="border-border" data-testid="top-events-list">
            <CardHeader>
              <CardTitle className="text-lg">Top events by attendance</CardTitle>
              <CardDescription>Most popular sessions</CardDescription>
            </CardHeader>
            <CardContent>
              {data.top_events.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-10">No events with attendees yet</p>
              ) : (
                <div className="space-y-3">
                  {data.top_events.map((ev, i) => (
                    <div key={ev.event_id} className="flex items-center gap-3 p-3 rounded-lg bg-muted/40">
                      <div className="w-8 h-8 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-sm font-medium flex-shrink-0">
                        {i + 1}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-sm truncate">{ev.title}</p>
                      </div>
                      <Badge variant="secondary">{ev.attendees} {ev.attendees === 1 ? 'attendee' : 'attendees'}</Badge>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Sponsorship funnel by tier + Member Sponsorship summary */}
        <div className="grid lg:grid-cols-3 gap-6 mb-6">
          <Card className="border-border lg:col-span-2" data-testid="sponsorship-funnel-by-tier">
            <CardHeader>
              <CardTitle className="text-lg">Sponsorship funnel by tier</CardTitle>
              <CardDescription>Which tiers actually convert — submitted, approved &amp; paid breakdown.</CardDescription>
            </CardHeader>
            <CardContent>
              {tierFunnel.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-10">
                  No sponsorship EOIs submitted yet. Share the public Sponsorship link from any event to start collecting submissions.
                </p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm" data-testid="tier-funnel-table">
                    <thead>
                      <tr className="text-left border-b border-border">
                        <th className="py-2 px-3 font-medium text-muted-foreground">Tier</th>
                        <th className="py-2 px-3 font-medium text-muted-foreground text-right">Submitted</th>
                        <th className="py-2 px-3 font-medium text-muted-foreground text-right">Approved</th>
                        <th className="py-2 px-3 font-medium text-muted-foreground text-right">Paid</th>
                        <th className="py-2 px-3 font-medium text-muted-foreground text-right">Conversion</th>
                        <th className="py-2 px-3 font-medium text-muted-foreground text-right">Revenue (AUD)</th>
                      </tr>
                    </thead>
                    <tbody>
                      {tierFunnel.map((row) => {
                        const conversion = row.submitted > 0
                          ? Math.round((row.paid / row.submitted) * 1000) / 10
                          : 0;
                        return (
                          <tr key={row.tier_name} className="border-b border-border/60" data-testid={`tier-row-${row.tier_name}`}>
                            <td className="py-2 px-3 font-medium">{row.tier_name}</td>
                            <td className="py-2 px-3 text-right">{row.submitted}</td>
                            <td className="py-2 px-3 text-right">{row.approved}</td>
                            <td className="py-2 px-3 text-right text-emerald-700 font-semibold">{row.paid}</td>
                            <td className="py-2 px-3 text-right">{conversion}%</td>
                            <td className="py-2 px-3 text-right font-medium">${Number(row.revenue_aud || 0).toLocaleString('en-AU')}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>

          <Card className="border-border" data-testid="member-sponsorship-summary-card">
            <CardHeader>
              <CardTitle className="text-lg">Member Sponsorship</CardTitle>
              <CardDescription>Tax-deductible contributions (separate from event sponsorships).</CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex items-baseline justify-between">
                <span className="text-sm text-muted-foreground">Paid contributions</span>
                <span className="text-2xl font-bold" data-testid="member-paid-count">{memberSummary.paid_count}</span>
              </div>
              <div className="flex items-baseline justify-between">
                <span className="text-sm text-muted-foreground">Awaiting payment</span>
                <span className="text-lg font-medium" data-testid="member-unpaid-count">{memberSummary.unpaid_count}</span>
              </div>
              <div className="flex items-baseline justify-between border-t border-border pt-3">
                <span className="text-sm text-muted-foreground">Revenue captured</span>
                <span className="text-lg font-semibold text-emerald-700" data-testid="member-paid-revenue">
                  AUD ${Number(memberSummary.paid_revenue || 0).toLocaleString('en-AU')}
                </span>
              </div>
              {data.sponsorship_member_discounts_given > 0 && (
                <div className="flex items-baseline justify-between text-xs text-muted-foreground pt-2">
                  <span>IIDA member discounts granted on event sponsorships</span>
                  <span className="font-medium">AUD ${Number(data.sponsorship_member_discounts_given).toLocaleString('en-AU')}</span>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Feedback summary */}
        <Card className="border-border mb-6" data-testid="feedback-summary-chart">
          <CardHeader>
            <CardTitle className="text-lg">Feedback summary across all events</CardTitle>
            <CardDescription>
              Aggregate Yes/No responses. "No commercial bias" is inverted (positive = "no" answer in the form).
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="h-[280px]">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={feedbackData} layout="vertical" margin={{ left: 30 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis type="number" allowDecimals={false} tick={{ fontSize: 11 }} />
                  <YAxis type="category" dataKey="question" tick={{ fontSize: 11 }} width={130} />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="yes" stackId="a" fill="#10b981" />
                  <Bar dataKey="no" stackId="a" fill="#ef4444" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
}
